<template>
<div class="homepage-container scroll-smooth" :class="'route-'+$route.name" :style="{height: innerHeight + 'px'}">

    <div class="bg-white snap-start sticky top-0 z-50 shadow-xl">
        <div class="mx-auto max-w-screen-2xl px-2 md:px-8">
            <header class="flex items-center justify-between border-b py-4 md:py-4 bg-white w-full">
                <!-- logo - start -->
                <router-link to="/" class="inline-flex items-center gap-2.5 text-2xl font-bold text-black md:text-3xl" aria-label="logo">
                    <img src="../assets/images/logo.png" :style="{height: $parent.isMobile ? '32px' : '36px'}" />
                </router-link>
                <!-- logo - end -->

                <!-- nav - start -->
                <nav class="hidden gap-12 md:flex lg:flex xl:flex items-center">
                    <vs-button @click="$router.push('/explore/premium')" primary  size="large" class="animate">
                        <i class='bx bx-crown bx-sm'></i>
                        {{ $t('homepage.nav.premium') }}
                    </vs-button>
                    <router-link to="/blog" class="text-lg font-semibold text-gray-600 transition duration-100 hover:text-tourial active:text-indigo-700">{{ $t('common.blog') }}</router-link>
                    <router-link to="/contact-us" class="text-lg font-semibold text-gray-600 transition duration-100 hover:text-tourial active:text-indigo-700">{{ $t('common.contactUs') }}</router-link>
                    <vs-button @click="$router.push('/explore/login')" primary size="large">{{ $t('login.signIn') }}</vs-button>

                </nav>
                <!-- nav - end -->


                <vs-button @click="$router.push('/explore/premium')" primary  size="large" class="animate" v-if="$parent.isMobile" style="margin:0;">
                    <i class='bx bx-crown bx-sm'></i>
                    {{ $t('homepage.nav.premium') }}
                </vs-button>

                <!-- buttons - start -->

                <vs-button size="large" dark @click="() => {$parent.mobileMenuVisible = !$parent.mobileMenuVisible}" v-if="$parent.isMobile" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" />
                    </svg>

                </vs-button>


                <!-- buttons - end -->
            </header>
        </div>
    </div>

    <div class="homepage-hero-container relative">
    <!-- Background Video -->
    <video 
        class="absolute inset-0 w-full h-full object-cover" 
        autoplay 
        loop 
        muted 
        playsinline
        @error="videoUnavailable = true"
    >
        <source src="../assets/videos/hero.webm" type="video/webm" />
        <source src="../assets/videos/hero.mp4" type="video/mp4" />
        {{ $t('homepage.hero.videoNotSupported') }}
    </video>

    <div class="pb-6 sm:pb-8 lg:pb-12 homepage-hero relative z-10" >
            <div class="mx-auto max-w-screen-2xl px-2 md:px-6 h-full">
                <section class="flex flex-col items-center h-full">
                    <div class="flex h-full flex-col items-center justify-center text-center" v-if="videoUnavailable">
                        <h1 class="text-4xl font-bold text-white sm:text-5xl mb-2 md:mb-10 lg:mb-10 xl:mb-10 md:text-8xl">
                            {{ $t('homepage.hero.titleLine1') }}<br />{{ $t('homepage.hero.titleLine2') }}
                        </h1>
                        <vs-button 
                            :block="$parent.isMobile" 
                            @click="$router.push('/explore')" 
                            class="mx-0 mb-10" 
                            size="large"
                        >
                            {{ $t('homepage.hero.cta') }}
                        </vs-button>
                    </div>
                </section>
            </div>
        </div>
    </div>


    <div class="bg-white py-12 bg-opacity-30  flex flex-col justify-center snap-start">
        <div class="mx-auto max-w-screen-2xl px-4 md:px-8">
            <!--<p class="mt-10 mb-2 text-center font-semibold text-tourial md:mb-3 lg:text-lg">Introducing</p>-->

            <h2 class="mb-4 text-center text-2xl text-gray-800 md:mb-6 lg:text-3xl"><span class="font-bold">{{ $t('homepage.intro.heading') }}</span></h2>

            <h3 class="mb-4 text-center text-xl font-bold text-gray-800 md:mb-6 lg:text-2xl">{{ $t('homepage.intro.subheading') }}</h3>

            <p class="mx-auto w-full text-center text-gray-700 md:text-lg mb-3">{{ $t('homepage.intro.paragraph1Before') }}<span class="font-bold">{{ $t('homepage.intro.paragraph1Bold') }}</span>{{ $t('homepage.intro.paragraph1After') }}</p>

            <p class="mx-auto w-full text-center text-gray-700 md:text-lg mb-5"><span class="font-bold">{{ $t('homepage.intro.paragraph2Bold') }}</span>{{ $t('homepage.intro.paragraph2After') }}</p>
            <div class="flex w-full flex-col gap-2.5 sm:flex-row sm:justify-center">
                <vs-button @click="redirectToAppOrLogin" style="margin:0;" size="large">
                    <template v-if="!$parent.isMobile">{{ $t('homepage.cta.login') }}</template>
                    <template v-else>{{ $t('homepage.cta.downloadApp') }}</template>
                </vs-button>

            </div>
        </div>
    </div>
    <div class="bg-white py-12  flex flex-col justify-center snap-start">
        <carousel :perPage="1" :autoplay="false" :minSwipeDistance="3" :navigationEnabled="!$parent.isMobile" :class="{'dynamic-slide-x': !$parent.isMobile}">
            <slide>
                <div class="mx-auto max-w-screen-xl px-4">
                    <div class="grid gap-8 md:grid-cols-2 lg:gap-12">
                        <div>
                            <div class="flex flex-col justify-center items-center h-full">
                                <img src="../assets/images/tourial-explore-m.png" loading="lazy" :alt="$t('homepage.carousel.slide1.imageAlt')" style="max-height: 600px; max-width: 100%; width: auto; filter: brightness(1.1);" />
                            </div>
                        </div>

                        <div class="md:pt-8 flex flex-col justify-center">
                            <p class="text-center font-bold text-tourial md:text-left">{{ $t('homepage.carousel.eyebrow') }}</p>

                            <h1 class="mb-4 text-center text-3xl font-extrabold text-gray-800 sm:text-4xl md:mb-6 md:text-left">{{ $t('homepage.carousel.slide1.title') }}</h1>

                            <p class="mb-2 text-gray-500 text-lg md:text-xl text-center md:text-left">
                                {{ $t('homepage.carousel.slide1.textPart1') }}<span class="text-tourial font-bold">{{ $t('homepage.carousel.slide1.textBold1') }}</span>{{ $t('homepage.carousel.slide1.textPart2') }}<span class="text-tourial font-bold">{{ $t('homepage.carousel.slide1.textBold2') }}</span>
                            </p>
                            <div class="my-5 flex justify-center md:justify-start">
                                <vs-button :block="$parent.isMobile" @click="redirectToAppOrLogin" class="mx-0" size="large">
                                    <template v-if="!$parent.isMobile">{{ $t('homepage.cta.login') }}</template>
                                    <template v-else>{{ $t('homepage.cta.downloadApp') }}</template>
                                </vs-button>
                            </div>
                        </div>
                    </div>

                </div>
            </slide>
            <slide>
                <div class="mx-auto max-w-screen-xl px-4">
                    <div class="grid gap-8 md:grid-cols-2 lg:gap-12">
                        <div>
                            <div class="flex flex-col justify-center items-center h-full">
                                <img src="../assets/images/tourial-trip-m.png" loading="lazy" :alt="$t('homepage.carousel.slide2.imageAlt')" style="max-height: 600px; max-width: 100%; width: auto; filter: brightness(1.1);" />
                            </div>
                        </div>

                        <div class="md:pt-8 flex flex-col justify-center">
                            <p class="text-center font-bold text-tourial md:text-left">{{ $t('homepage.carousel.eyebrow') }}</p>

                            <h1 class="mb-4 text-center text-3xl font-extrabold text-gray-800 sm:text-4xl md:mb-6 md:text-left">{{ $t('homepage.carousel.slide2.title') }}</h1>

                            <p class="mb-2 text-gray-500 text-lg md:text-xl text-center md:text-left">
                                {{ $t('homepage.carousel.slide2.textPart1') }}<span class="text-tourial font-bold">{{ $t('homepage.carousel.slide2.textBold1') }}</span>{{ $t('homepage.carousel.slide2.textPart2') }}<span class="text-tourial font-bold">{{ $t('homepage.carousel.slide2.textBold2') }}</span>
                            </p>
                            <div class="my-5 flex justify-center md:justify-start">
                                <vs-button :block="$parent.isMobile" @click="redirectToAppOrLogin" class="mx-0" size="large">
                                    <template v-if="!$parent.isMobile">{{ $t('homepage.cta.login') }}</template>
                                    <template v-else>{{ $t('homepage.cta.downloadApp') }}</template>
                                </vs-button>
                            </div>

                        </div>
                    </div>

                </div>
            </slide>
            <slide>
                <div class="mx-auto max-w-screen-xl px-4">
                    <div class="grid gap-8 md:grid-cols-2 lg:gap-12">
                        <div>
                            <div class="flex flex-col justify-center items-center h-full">
                                <img src="../assets/images/tourial-blog-m.png" loading="lazy" :alt="$t('homepage.carousel.slide3.imageAlt')" style="max-height: 600px; max-width: 100%; width: auto; filter: brightness(1.1);" />
                            </div>
                        </div>

                        <div class="md:pt-8 flex flex-col justify-center">
                            <p class="text-center font-bold text-tourial md:text-left">{{ $t('homepage.carousel.eyebrow') }}</p>

                            <h1 class="mb-4 text-center text-3xl font-extrabold text-gray-800 sm:text-4xl md:mb-6 md:text-left">{{ $t('homepage.carousel.slide3.title') }}</h1>

                            <p class="mb-2 text-gray-500 text-lg md:text-xl text-center md:text-left">
                                {{ $t('homepage.carousel.slide3.text') }}
                            </p>
                            <div class="my-5 flex justify-center md:justify-start">
                                <vs-button :block="$parent.isMobile" @click="redirectToAppOrLogin" class="mx-0" size="large">
                                    <template v-if="!$parent.isMobile">{{ $t('homepage.cta.login') }}</template>
                                    <template v-else>{{ $t('homepage.cta.downloadApp') }}</template>
                                </vs-button>
                            </div>

                        </div>
                    </div>

                </div>
            </slide>

        </carousel>

    </div>
    <div class="bg-white py-12 bg-opacity-30 flex flex-col justify-center snap-start snap-start">
        <div class="mx-auto w-full px-4" style="box-sizing: border-box;">
            <!-- text - start -->
            <div class="mb-10 md:mb-16 px-8">
                <h2 class="mb-4 text-center text-2xl font-bold text-gray-800 md:mb-6 lg:text-3xl">{{ $t('homepage.blog.heading') }}</h2>

                <p class="mx-auto w-full text-center text-gray-700 md:text-lg">{{ $t('homepage.blog.description') }}</p>
            </div>
            <!-- text - end -->

            <carousel v-if="articles.length > 0" :perPageCustom="$parent.isMobile ? [[1,1]] : [[1, 3]]" :autoplay="true" :navigationEnabled="!$parent.isMobile" :minSwipeDistance="3" class="mt-10 mb-6" :class="{'dynamic-slide-x': !$parent.isMobile}">
                <slide v-for="article in articles" :key="'article-' + article.id">
                    <!-- article - start -->
                    <router-link :to="('/blog/post/' + (article.slug != undefined ? article.slug : article.id))" class="group relative flex h-56 flex-col overflow-hidden rounded-lg bg-gray-100 shadow-lg md:h-64 xl:h-96 mb-5" :class="{'mx-5': !$parent.isMobile}">
                        <img :src="article.picture_url" loading="lazy" :alt="article.title" class="absolute inset-0 h-full w-full object-cover object-center transition duration-200 group-hover:scale-110" />

                        <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-800 to-transparent md:via-transparent"></div>

                        <div class="relative mt-auto p-4">
                            <span class="block text-sm text-gray-200">{{ (new Date(article.updated_at)).toLocaleDateString() }}</span>
                            <h2 class="mb-2 text-xl font-semibold text-white transition duration-100">{{ article.title }}</h2>

                            <span class="font-semibold text-tourial">{{ $t('homepage.blog.readMore') }}</span>
                        </div>
                    </router-link>
                </slide>
            </carousel>
            <!-- article - end -->

            <div class="flex w-full flex-col gap-2.5 sm:flex-row sm:justify-center mt-5">
                <vs-button @click="$router.push('/blog')" size="large" style="margin: 0;">{{ $t('homepage.blog.cta') }}</vs-button>

            </div>
        </div>
    </div>
    <!-- FAQ Section -->
    <div class="bg-white py-16 snap-start">
        <div class="mx-auto max-w-screen-xl px-4 md:px-8">
            <div class="mb-4 text-center">
                <p class="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3">{{ $t('homepage.faq.eyebrow') }}</p>
                <h2 class="mb-8 text-center text-2xl font-bold text-gray-800 lg:text-3xl">{{ $t('homepage.faq.heading') }}</h2>
            </div>

            <div class="hidden md:flex gap-3">
                <!-- Column 1: even indices (always visible first 3, then extra) -->
                <div class="flex flex-col gap-3 flex-1">
                    <div
                        v-for="(faq, index) in faqs.filter((_, i) => i % 2 === 0 && i < 6)"
                        :key="'faq-left-base-' + index"
                        class="homepage-faq-card rounded-2xl border border-gray-100 px-5 py-3 cursor-pointer"
                        :class="{ 'homepage-faq-card--open': openFaq === faqs.indexOf(faq) }"
                        @click="openFaq = openFaq === faqs.indexOf(faq) ? null : faqs.indexOf(faq)"
                    >
                        <div class="flex items-start justify-between gap-4">
                            <span class="text-base font-semibold text-gray-800 faq-question-text">{{ faq.question }}</span>
                            <span class="faq-chevron mt-0.5 flex-shrink-0" :class="{ 'faq-chevron--open': openFaq === faqs.indexOf(faq) }">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </div>
                        <div class="faq-answer-wrapper" :class="{ 'faq-answer-wrapper--open': openFaq === faqs.indexOf(faq) }">
                            <p class="faq-answer-text text-gray-500 text-sm leading-relaxed pt-3">{{ faq.answer }}</p>
                        </div>
                    </div>
                    <transition-group name="faq-expand" tag="div" class="flex flex-col gap-3">
                        <div
                            v-for="(faq, index) in faqs.filter((_, i) => i % 2 === 0 && i >= 6)"
                            v-show="showAllFaqs"
                            :key="'faq-left-extra-' + index"
                            class="homepage-faq-card rounded-2xl border border-gray-100 px-5 py-3 cursor-pointer"
                            :class="{ 'homepage-faq-card--open': openFaq === faqs.indexOf(faq) }"
                            @click="openFaq = openFaq === faqs.indexOf(faq) ? null : faqs.indexOf(faq)"
                        >
                            <div class="flex items-start justify-between gap-4">
                                <span class="text-base font-semibold text-gray-800 faq-question-text">{{ faq.question }}</span>
                                <span class="faq-chevron mt-0.5 flex-shrink-0" :class="{ 'faq-chevron--open': openFaq === faqs.indexOf(faq) }">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </span>
                            </div>
                            <div class="faq-answer-wrapper" :class="{ 'faq-answer-wrapper--open': openFaq === faqs.indexOf(faq) }">
                                <p class="faq-answer-text text-gray-500 text-sm leading-relaxed pt-3">{{ faq.answer }}</p>
                            </div>
                        </div>
                    </transition-group>
                </div>
                <!-- Column 2: odd indices -->
                <div class="flex flex-col gap-3 flex-1">
                    <div
                        v-for="(faq, index) in faqs.filter((_, i) => i % 2 === 1 && i < 6)"
                        :key="'faq-right-base-' + index"
                        class="homepage-faq-card rounded-2xl border border-gray-100 px-5 py-3 cursor-pointer"
                        :class="{ 'homepage-faq-card--open': openFaq === faqs.indexOf(faq) }"
                        @click="openFaq = openFaq === faqs.indexOf(faq) ? null : faqs.indexOf(faq)"
                    >
                        <div class="flex items-start justify-between gap-4">
                            <span class="text-base font-semibold text-gray-800 faq-question-text">{{ faq.question }}</span>
                            <span class="faq-chevron mt-0.5 flex-shrink-0" :class="{ 'faq-chevron--open': openFaq === faqs.indexOf(faq) }">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </div>
                        <div class="faq-answer-wrapper" :class="{ 'faq-answer-wrapper--open': openFaq === faqs.indexOf(faq) }">
                            <p class="faq-answer-text text-gray-500 text-sm leading-relaxed pt-3">{{ faq.answer }}</p>
                        </div>
                    </div>
                    <transition-group name="faq-expand" tag="div" class="flex flex-col gap-3">
                        <div
                            v-for="(faq, index) in faqs.filter((_, i) => i % 2 === 1 && i >= 6)"
                            v-show="showAllFaqs"
                            :key="'faq-right-extra-' + index"
                            class="homepage-faq-card rounded-2xl border border-gray-100 px-5 py-3 cursor-pointer"
                            :class="{ 'homepage-faq-card--open': openFaq === faqs.indexOf(faq) }"
                            @click="openFaq = openFaq === faqs.indexOf(faq) ? null : faqs.indexOf(faq)"
                        >
                            <div class="flex items-start justify-between gap-4">
                                <span class="text-base font-semibold text-gray-800 faq-question-text">{{ faq.question }}</span>
                                <span class="faq-chevron mt-0.5 flex-shrink-0" :class="{ 'faq-chevron--open': openFaq === faqs.indexOf(faq) }">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </span>
                            </div>
                            <div class="faq-answer-wrapper" :class="{ 'faq-answer-wrapper--open': openFaq === faqs.indexOf(faq) }">
                                <p class="faq-answer-text text-gray-500 text-sm leading-relaxed pt-3">{{ faq.answer }}</p>
                            </div>
                        </div>
                    </transition-group>
                </div>
            </div>
            <!-- Mobile: single column -->
            <div class="flex flex-col gap-3 md:hidden">
                <div
                    v-for="(faq, index) in faqs.slice(0, 6)"
                    :key="'faq-mobile-base-' + index"
                    class="homepage-faq-card rounded-2xl border border-gray-100 px-5 py-3 cursor-pointer"
                    :class="{ 'homepage-faq-card--open': openFaq === index }"
                    @click="openFaq = openFaq === index ? null : index"
                >
                    <div class="flex items-start justify-between gap-4">
                        <span class="text-base font-semibold text-gray-800 faq-question-text">{{ faq.question }}</span>
                        <span class="faq-chevron mt-0.5 flex-shrink-0" :class="{ 'faq-chevron--open': openFaq === index }">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </span>
                    </div>
                    <div class="faq-answer-wrapper" :class="{ 'faq-answer-wrapper--open': openFaq === index }">
                        <p class="faq-answer-text text-gray-500 text-sm leading-relaxed pt-3">{{ faq.answer }}</p>
                    </div>
                </div>
                <transition-group name="faq-expand" tag="div" class="flex flex-col gap-3">
                    <div
                        v-for="(faq, index) in faqs.slice(6)"
                        v-show="showAllFaqs"
                        :key="'faq-mobile-extra-' + index"
                        class="homepage-faq-card rounded-2xl border border-gray-100 px-5 py-3 cursor-pointer"
                        :class="{ 'homepage-faq-card--open': openFaq === (index + 6) }"
                        @click="openFaq = openFaq === (index + 6) ? null : (index + 6)"
                    >
                        <div class="flex items-start justify-between gap-4">
                            <span class="text-base font-semibold text-gray-800 faq-question-text">{{ faq.question }}</span>
                            <span class="faq-chevron mt-0.5 flex-shrink-0" :class="{ 'faq-chevron--open': openFaq === (index + 6) }">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </div>
                        <div class="faq-answer-wrapper" :class="{ 'faq-answer-wrapper--open': openFaq === (index + 6) }">
                            <p class="faq-answer-text text-gray-500 text-sm leading-relaxed pt-3">{{ faq.answer }}</p>
                        </div>
                    </div>
                </transition-group>
            </div>

            <div class="mt-10 flex w-full flex-col gap-2.5 sm:flex-row sm:justify-center">
                <vs-button @click="showAllFaqs = !showAllFaqs" size="large" style="margin: 0;">
                    {{ showAllFaqs ? $t('homepage.faq.showLess') : $t('homepage.faq.showAll') }}
                </vs-button>
            </div>
        </div>
    </div>

    <div class="homepage-video-section py-12 snap-start">
        <div class="mx-auto max-w-screen-2xl px-4 md:px-8">
            <!-- text - start -->
            <div class="mb-10 md:mb-16">
                <h2 class="mb-4 text-center text-2xl font-bold text-white md:mb-6 lg:text-3xl">{{ $t('homepage.video.heading') }}</h2>

                <p class="mx-auto w-full text-center text-gray-200 md:text-lg">{{ $t('homepage.video.description') }}</p>
            </div>
            <!-- text - end -->

            <div class="flex flex-row justify-center">
                <!-- image - start -->

                <a @click="() => {youtubePlayer = true}" class="group relative flex h-48 items-end justify-end overflow-hidden rounded-lg bg-gray-100 shadow-lg md:h-96 " style="cursor: pointer;width: 640px;">
                    <img v-show="!youtubePlayer" src="../assets/images/thumbnail.png" loading="lazy" class="absolute inset-0 h-full w-full object-cover object-center transition duration-200 group-hover:scale-110" />
                    <div v-show="youtubePlayer" id="ytplayer"></div>

                    <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-800 via-transparent to-transparent opacity-50"></div>

                    <span class="relative mr-3 mb-3 inline-block rounded-lg border border-gray-500 px-2 py-1 text-xs text-gray-200 backdrop-blur md:px-3 md:text-sm">YouTube</span>
                </a>
                <!-- image - end -->
            </div>
        </div>
    </div>
    <footer class="bg-white" style="border-top: 1px solid rgb(199, 199, 199);">
        <div class="pb-16 pt-4 sm:pt-10 lg:pt-12">
            <div class="mx-auto max-w-screen-2xl px-4 md:px-8">
                <div class="grid grid-cols-1 gap-12 border-t pt-10 md:grid-cols-4 lg:grid-cols-6 lg:gap-8 lg:pt-12">
                    <div class="col-span-full lg:col-span-2">
                        <!-- logo - start -->
                        <div class="mb-4 lg:-mt-2">
                            <router-link to="/" class="inline-flex items-center gap-2 text-xl font-bold text-black md:text-2xl" aria-label="logo">
                                <img src="../assets/images/logo.png" style="height: 36px;"/>

                            </router-link>
                        </div>
                        <!-- logo - end -->

                        <!--<p class="text-gray-500 sm:pr-8">Sei pronto a viaggiare con Tourial? Arriva il sito web definitivo per esplorare e pianificare le tue avventure, tutto a portata di mano.</p>-->
                    </div>

                    <!-- nav - start -->
                    <div>
                        <div class="mb-4 font-bold uppercase tracking-widest text-gray-800">{{ $t('homepage.footer.usefulLinks') }}</div>

                        <nav class="flex flex-col gap-4">
                            <div>
                                <router-link to="/privacy-policy" class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">{{ $t('common.privacyPolicy') }}</router-link>
                            </div>
                            <div>
                                <router-link to="/cookie-policy" class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">{{ $t('homepage.footer.links.cookiePolicy') }}</router-link>
                            </div>

                            <div>
                                <router-link to="/tos" class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">{{ $t('common.termsOfService') }}</router-link>
                            </div>
                        </nav>
                    </div>
                    <!-- nav - end -->

                    <!-- nav - start -->
                    <div>
                        <div class="mb-4 font-bold uppercase tracking-widest text-gray-800">{{ $t('homepage.footer.contacts.heading') }}</div>

                        <nav class="flex flex-col gap-4">
                            <div>
                                <a class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">{{ $t('homepage.footer.contacts.founderRole') }}</a>
                            </div>

                            <div>
                                <a class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">Elena Colombo</a>
                            </div>

                            <div>
                                <a class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">Via Casati 25, Lesmo (MB)</a>
                            </div>

                            <div>
                                <a href="mailto:info@tourial.it" class="text-gray-500 transition duration-100 hover:text-tourial active:text-indigo-600">info@tourial.it</a>
                            </div>
                        </nav>
                    </div>
                    <!-- nav - end -->
                     <!-- nav - start -->
                     <div>
                        <div class="mb-4 font-bold uppercase tracking-widest text-gray-800">{{ $t('homepage.footer.downloadApp.heading') }}</div>

                        <nav class="flex flex-col gap-4">
                            <a href="https://apps.apple.com/it/app/tourial/id6739505239" target="_blank" class="transition duration-100">
                                <img src="../assets/icons/app-store-badge.png" alt="App Store" class="h-10" />
                            </a>
                            
                            <a href="https://play.google.com/store/apps/details?id=it.wisedsrl.tourial" target="_blank" class="transition duration-100">
                                <img src="../assets/icons/google-play-badge.png" alt="Google Play" class="h-10" />
                            </a>
                        </nav>
                    </div>
                    <!-- nav - end -->
                    <!-- nav - start -->
                    <div>
                        <div class="mb-4 font-bold uppercase tracking-widest text-gray-800">{{ $t('homepage.footer.social.heading') }}</div>

                        <nav class="flex flex-row gap-4">
                            <!-- social - start -->
                            <a href="https://www.facebook.com/profile.php?id=100090998845042" target="_blank" class="text-gray-400 transition duration-100 hover:text-gray-500 active:text-gray-600">
                                <img src="../assets/icons/fb_orange.png" alt="Facebook" class="h-8 h-8" />
                            </a>
                            
                            <a href="https://instagram.com/tourial.it" target="_blank" class="text-gray-400 transition duration-100 hover:text-gray-500 active:text-gray-600">
                                <img src="../assets/icons/ig_orange.png" alt="Instagram" class="h-8 h-8" />
                            </a>


                            <a href="https://www.tiktok.com/@tourial.it" target="_blank" class="text-gray-400 transition duration-100 hover:text-gray-500 active:text-gray-600">
                                <img src="../assets/icons/tt_orange.png" alt="TikTok" class="h-8 h-8" />
                            </a>

                            <a href="https://www.youtube.com/@Tourial.it." target="_blank" class="text-gray-400 transition duration-100 hover:text-gray-500 active:text-gray-600">
                                <img src="../assets/icons/yt_orange.png" alt="YouTube" class="h-8 h-8" />
                            </a>
                            <!-- social - end -->

                            
                        </nav>
                    </div>

                   
                </div>
            </div>
        </div>

        <div class="bg-black bg-opacity-80">
            <div class="mx-auto max-w-screen-2xl px-4 md:px-8">
                <div class="flex items-center justify-between gap-4 py-8">
                    <span class="text-sm text-gray-400"> © 2024 - <span class="text-tourial">Tourial.</span>
                        {{ $t('homepage.footer.copyright.rightsReserved') }}<br v-if="$parent.isMobile" />
                        {{ $t('homepage.footer.copyright.companyVat') }} IT12881850965</span>

                </div>
            </div>
        </div>
    </footer>
</div>
</template>

<style scoped>
@import '../tailwind.css';
</style>

<script>
import {
    Carousel,
    Slide
} from 'vue-carousel';
import axios from 'axios';
import getEnv from '../utils/env.js';

function wpBaseUrl() {
    return (getEnv('WP_BLOG_URL') || 'https://tourial.it/blog').replace(/\/+$/, '');
}
export default {
    components: {
        Carousel,
        Slide
    },

    props: {
        innerHeight: {
            type: Number,
            default: 100,
            required: false
        }
    },

    data() {
        return {
            articles: [],
            youtubePlayer: false,
            videoUnavailable: false,
            openFaq: null,
            showAllFaqs: false,
            faqCount: 12
        }
    },

    computed: {
        faqs() {
            return Array.from({ length: this.faqCount }, (_, i) => ({
                question: this.$t(`homepage.faq.items.${i}.question`),
                answer: this.$t(`homepage.faq.items.${i}.answer`)
            }));
        },

        visibleFaqs() {
            return this.showAllFaqs ? this.faqs : this.faqs.slice(0, 6);
        }
    },

    methods: {
        getAppStoreUrl() {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            
            // iOS detection
            if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
                return 'https://apps.apple.com/it/app/tourial/id6739505239';
            }
            
            // Android detection
            if (/android/i.test(userAgent)) {
                return 'https://play.google.com/store/apps/details?id=it.wisedsrl.tourial';
            }
            
            // Default to Google Play (for other mobile devices)
            return 'https://play.google.com/store/apps/details?id=it.wisedsrl.tourial';
        },
        
        redirectToAppOrLogin() {
            if (this.$parent.isMobile) {
                window.location.href = this.getAppStoreUrl();
            } else {
                this.$router.push('/explore/login');
            }
        },

        async getBlogArticles(reset = true) {
            const loading = this.$vs.loading();

            try {
                const url = wpBaseUrl() + '/wp-json/wp/v2/posts?per_page=4&page=1&_embed&orderby=date&order=desc';
                const response = await axios.get(url, { headers: { Accept: 'application/json' } });

                if (response.status === 200 && Array.isArray(response.data)) {
                    const mapped = response.data.map(post => this.mapWpPost(post));
                    this.articles = reset ? mapped : this.articles.concat(mapped);
                } else {
                    this.articles = reset ? [] : this.articles;
                }
            } catch (e) {
                console.error('Failed to load WP posts for homepage:', e);

                if (reset) {
                    this.articles = [];
                }

                this.$vs.notification({
                    title: this.$t('common.messages.somethingWentWrong'),
                    text: this.$t('blog.messages.cannotLoadPosts'),
                    color: 'danger',
                    position: 'top-right'
                });
            } finally {
                loading.close();
            }
        },

        /**
         * Map a WP REST API post object to the format expected by the homepage carousel.
         */
        mapWpPost(post) {
            let pictureUrl = null;
            if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0]) {
                const media = post._embedded['wp:featuredmedia'][0];
                pictureUrl = media.source_url || null;
            }

            let metaDescription = '';
            if (post.yoast_head_json && post.yoast_head_json.description) {
                metaDescription = post.yoast_head_json.description;
            } else if (post.excerpt && post.excerpt.rendered) {
                metaDescription = post.excerpt.rendered.replace(/<[^>]+>/g, '').trim();
            }

            return {
                id: post.id,
                title: post.title?.rendered || '',
                text: post.content?.rendered || '',
                slug: post.slug,
                updated_at: post.date,
                picture_url: pictureUrl,
                picture_id: null,
                tags: [],
                meta_description: metaDescription,
                published: post.status === 'publish',
                _wp_source: true,
            };
        },
    },
    watch: {
        youtubePlayer(n) {
            if (n) {
                document.getElementById('ytplayer').style.display = 'block';
            } else {
                document.getElementById('ytplayer').style.display = 'none';
            }
        }
    },

    mounted() {
        this.getBlogArticles();
        // Load the IFrame Player API code asynchronously.
        var tag = document.createElement('script');
        tag.src = "https://www.youtube.com/player_api";
        var firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window.onYouTubePlayerAPIReady = function () {
            new window.YT.Player('ytplayer', {
                height: '360',
                width: '640',
                videoId: 'tvLmxfJwXQ4',
                autoplay: 1,
            });
        }
    }
}
</script>

<style>
.homepage-hero {
    /* background linear gradient overlayed on background image */
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.0));
    background-size: cover;
    height: calc(100dvh - 93px);
}

.homepage-hero-container {
    background-image: url('../assets/images/homepage-hero.jpg');
    background-size: cover;
    background-position-x: 22%;
    background-position-y: bottom;

}

.homepage-container {
    padding-top: 0 !important;
    overflow-x:hidden;
}

.group:hover img {
    transform: scale(1.2);
}

iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.VueCarousel-dot,
.VueCarousel-dot-container {
    margin-top: 0 !important;
}

.flashing {
    animation: flashing 3s infinite ease-in-out;
}

@keyframes flashing {

    0%,
    60%,
    100% {
        color: #fff;
        text-shadow: none
    }

    80% {
        color: rgba(var(--vs-primary));
        text-shadow: 0 0 1px rgba(var(--vs-primary));
    }
}

/* desktop only */
@media (min-width: 1024px) {

    .homepage-hero-container {
        background-position-x: center;
    }
}

.VueCarousel-navigation-prev, .VueCarousel-navigation-next {
    transform: unset!important;
}

.VueCarousel-navigation-button {
    background-color: rgba(var(--vs-primary))!important;
    color: transparent!important;
    border-radius: 100%!important;
    width: 50px!important;
    height: 50px!important;
    display: flex!important;
    align-items: center!important;
    justify-content: center!important;
    margin-left: 10px!important;
    margin-right: 10px!important;
    background-position: center;
    background-repeat: no-repeat;
    background-size: 50%;
    top: calc(50% - 50px)!important;
}

.VueCarousel-navigation-prev {
    left: 3%!important;
    background-image: url(../assets/icons/nav-left.png);

}

.VueCarousel-navigation-next {
    right: 3%!important;
    background-image: url(../assets/icons/nav-right.png);
}

.homepage-container a {
    text-decoration: none!important;
}

/* desktop only */
@media (min-width: 1024px) {
    .dynamic-slide-x {
        padding-left: calc(9% + 50px)!important;
        padding-right: calc(9% + 50px)!important;
    }
}

/* mobile only .homepage-hero must fit width and not height */
@media (max-width: 600px) {
    .homepage-hero {
        width: 100%;
        /* height 16/9 */
        height: calc(100vw * 9 / 18);

    }
}

/* Video section - transparent over hero background */
.homepage-video-section {
    background-image: url('../assets/images/homepage-hero.jpg');
    background-size: cover;
    background-position: center;
    position: relative;
}

.homepage-video-section::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
}

.homepage-video-section > * {
    position: relative;
    z-index: 1;
}

/* FAQ card micro-interactions */
.homepage-faq-card {
    background: #fff;
    transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.15s ease;
    will-change: transform;
}

.homepage-faq-card:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.09);
    border-color: #e0e7ff;
    transform: translateY(-1px);
}

.homepage-faq-card:active {
    transform: translateY(0px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.homepage-faq-card--open {
    border-color: rgba(var(--vs-primary), 0.3);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
}

/* Question text color transition on open */
.homepage-faq-card--open .faq-question-text {
    color: rgba(var(--vs-primary));
}

.faq-question-text {
    transition: color 0.2s ease;
}

/* Chevron rotation */
.faq-chevron {
    color: #9ca3af;
    transition: color 0.2s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-chevron--open {
    transform: rotate(180deg);
    color: rgba(var(--vs-primary));
}

/* Smooth accordion answer expand/collapse */
.faq-answer-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.25s ease;
    opacity: 0;
    overflow: hidden;
    margin: 0;
    padding: 0;
    min-height: 0;
    max-height: 0;
}

.faq-answer-wrapper--open {
    grid-template-rows: 1fr;
    opacity: 1;
    max-height: none;
}

.faq-answer-text {
    min-height: 0;
    overflow: hidden;
}

/* FAQ expand/collapse transition for "Vedi tutte" button */
.faq-expand-enter-active {
    animation: faqSlideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.faq-expand-leave-active {
    animation: faqSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes faqSlideDown {
    from {
        opacity: 0;
        transform: translateY(-12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes faqSlideUp {
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(-12px);
    }
}
</style>
